import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import moment from 'moment'
import { useSearchParams } from 'react-router-dom'

import { DatabaseModel } from 'app/models/databaseModel'
import { ConnectionStatus } from 'app/constants/connectionStatus'
import { getConnectionIcon } from 'app/common/converters/connectionStatusToIconConverter'
import { getConnectionStatusText } from 'app/common/converters/connectionStatusToTextConverter'
import { PRECISE_TIME_FORMAT } from 'app/constants/date'
import {
  deleteDatabasesRequest,
  fetchPagedDatabases,
  retryDatabaseMigrationRequest,
} from 'app/containers/administration/databaseQueries'

import { isAdmin } from 'app/services/local/roleService'
import { TableProvider, useTable } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminIcon } from '@/modules/shared/components'
import { Pagination } from '@/modules/shared/components/Pagination'
import {
  clearAdminSelection,
  setAdminSelectedCount,
  subscribeToAdminRemove,
} from '@/modules/shared/stores/adminSelectionStore'
import { toast } from '@/lib/toasts/api'
import TransferDatabaseModal from '@/modules/databases/components/TransferDatabaseModal'
import { AdminColumn, AdminTable, AdminToolbar } from '../shared/AdminTable'
import { formatSize } from '../shared/formatSize'

type Props = {
  currentCompanyId?: string
  onGoToCompanies: (database: DatabaseModel) => void
}

const PER_PAGE = 10

/**
 * Zakładka BAZY DANYCH panelu admina - funkcyjna, na react-query.
 *
 * Zastępuje `app/components/administration/databases.tsx`. Stronicowanie i sortowanie
 * po stronie serwera, tak jak dotąd; filtr po serwerze/bazie nadal przychodzi z URL-a
 * (link ze Serwerów).
 */
const AdminDatabasesPageContent: React.FC<Props> = ({
  currentCompanyId,
  onGoToCompanies,
}) => {
  const { t } = useLocalize()
  const { selectedGroup, resetAll } = useTable()
  const [searchParams] = useSearchParams()

  const serverId = searchParams.get('filterByServer')
  const databaseId = searchParams.get('filterByDatabase')

  const [page, setPage] = React.useState(1)
  const [pageSize, setPageSize] = React.useState(PER_PAGE)
  const [orderBy, setOrderBy] = React.useState('name')
  const [descending, setDescending] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [debounced, setDebounced] = React.useState('')
  const [stalledOnly, setStalledOnly] = React.useState(false)
  const [retrying, setRetrying] = React.useState(false)
  const [transferTarget, setTransferTarget] =
    React.useState<DatabaseModel | null>(null)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(search)
      setPage(1)
    }, 500)
    return () => clearTimeout(timer)
  }, [search])

  const query = useQuery({
    queryKey: [
      'admin',
      'databases',
      currentCompanyId ?? null,
      pageSize,
      page,
      orderBy,
      descending,
      debounced,
      serverId,
      databaseId,
      stalledOnly,
    ],
    queryFn: () =>
      fetchPagedDatabases(
        currentCompanyId,
        pageSize,
        pageSize * (page - 1),
        orderBy,
        descending,
        debounced,
        serverId ? Number(serverId) : undefined,
        databaseId ? Number(databaseId) : undefined,
        stalledOnly,
      ),
    refetchInterval: 30000,
  })

  const databases: DatabaseModel[] = query.data?.databases ?? []
  const total: number = query.data?.databasesCount ?? 0

  const selectedCount = selectedGroup?.ids.length ?? 0
  React.useEffect(() => {
    setAdminSelectedCount(selectedCount)
  }, [selectedCount])
  React.useEffect(() => () => clearAdminSelection(), [])

  const selectedRef = React.useRef<Array<string | number>>([])
  selectedRef.current = selectedGroup?.ids ?? []
  React.useEffect(
    () =>
      subscribeToAdminRemove(async () => {
        const ids = selectedRef.current.map(String)
        if (ids.length === 0) return
        const result = await deleteDatabasesRequest(ids)
        const failed = result.filter((item: any) => !item.success)
        if (failed.length > 0) {
          toast.error({
            title: t('administrationPanel-databases.databases-deletion-failed'),
          })
        } else {
          toast.success({
            title: t('administrationPanel-databases.databases-deleted'),
          })
        }
        resetAll()
        clearAdminSelection()
        await query.refetch()
      }),
    [resetAll, query, t],
  )

  const retryMigration = async () => {
    const ids = selectedRef.current.map(String)
    if (ids.length === 0) return
    setRetrying(true)
    try {
      await retryDatabaseMigrationRequest(ids)
      toast.success({
        title: t('administrationPanel-databases.migration-retry-queued'),
      })
      resetAll()
      clearAdminSelection()
      await query.refetch()
    } catch {
      toast.error({
        title: t('administrationPanel-databases.migration-retry-failed'),
      })
    } finally {
      setRetrying(false)
    }
  }

  const sort = (key: string) => {
    setDescending(orderBy === key ? !descending : false)
    setOrderBy(key)
    setPage(1)
  }

  const columns: Array<AdminColumn<DatabaseModel>> = [
    {
      key: 'name',
      header: t('administrationPanel-databases.name'),
      width: 290,
      sortable: true,
    },
    {
      key: 'status',
      header: t('administrationPanel-servers.status'),
      width: 160,
      render: (database) => {
        const statusText = t(getConnectionStatusText(database.status))
        const title =
          database.status === ConnectionStatus.NOT_CONNECTED &&
          database.lastErrorMessage != null
            ? `${statusText}: ${database.lastErrorMessage}`
            : statusText
        return (
          <span className="tw-inline-flex tw-items-center tw-gap-1">
            <AdminIcon
              name={getConnectionIcon(database.status)}
              title={title}
            />
            {statusText}
          </span>
        )
      },
    },
    {
      key: 'serverName',
      header: t('administrationPanel-databases.server'),
      width: 270,
    },
    {
      key: 'migrationStatus',
      header: t('administrationPanel-databases.migration-status'),
      width: 170,
      render: (database) => {
        const failed =
          database.migrationStalled || database.migrationStatus === 'ERROR'
        const pending =
          !failed &&
          database.migrationStatus != null &&
          database.migrationStatus !== 'SUCCESS'
        const label = database.migrationStalled
          ? `${t('administrationPanel-databases.migration-stalled')} (${database.migrationFailedAttempts ?? 0})`
          : database.migrationStatus == null
            ? '?'
            : failed
              ? t('answer.no')
              : pending
                ? t('administrationPanel-databases.migration-pending')
                : t('answer.yes')
        return (
          <span
            className="tw-inline-flex tw-items-center tw-gap-1"
            title={
              failed || pending
                ? (database.lastMigrationErrorMessage ?? undefined)
                : undefined
            }
          >
            <AdminIcon
              name={getConnectionIcon(
                database.migrationStalled ? 'ERROR' : database.migrationStatus,
              )}
            />
            {label}
          </span>
        )
      },
    },
    {
      key: 'currentVersion',
      header: t('administrationPanel-databases.current-version'),
      width: 180,
      render: (database) =>
        database.currentVersion?.includes('_')
          ? database.currentVersion.substring(
              0,
              database.currentVersion.indexOf('_'),
            )
          : '?',
    },
    { key: 'createdBy', header: t('auditFields.created-by') },
    {
      key: 'createdDate',
      header: t('auditFields.created-date'),
      width: 180,
      sortable: true,
      render: (database) =>
        database.createdDate
          ? moment.utc(database.createdDate).local().format(PRECISE_TIME_FORMAT)
          : '',
    },
    {
      key: 'driversCount',
      header: t('administrationPanel-databases.drivers-count'),
      width: 100,
      align: 'right',
      sortable: true,
    },
    {
      key: 'size',
      header: t('administrationPanel-databases.size'),
      width: 110,
      align: 'right',
      sortable: true,
      render: (database) => formatSize(database.size),
    },
    {
      key: 'companiesCount',
      header: t('administrationPanel-databases.company'),
      width: 90,
      align: 'center',
      render: (database) => (
        <AdminIcon
          name="arrow-right"
          title={t('administrationPanel-databases.go-to-companies')}
          onClick={(event) => {
            event.stopPropagation()
            onGoToCompanies(database)
          }}
        />
      ),
    },
    ...(isAdmin()
      ? [
          {
            key: 'transfer',
            header: '',
            width: 130,
            render: (database: DatabaseModel) => (
              <a
                className="et0-link tw-cursor-pointer"
                title={t('administrationPanel-databases.transfer-title')}
                onClick={(event) => {
                  event.stopPropagation()
                  setTransferTarget(database)
                }}
              >
                {t('administrationPanel-databases.transfer-action')}
              </a>
            ),
          },
        ]
      : []),
  ]

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-content-primary">
      <AdminToolbar search={search} onSearch={setSearch}>
        <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm">
          <input
            type="checkbox"
            checked={stalledOnly}
            onChange={(event) => {
              setStalledOnly(event.target.checked)
              setPage(1)
            }}
          />
          {t('administrationPanel-databases.migration-stalled-filter')}
        </label>
        {isAdmin() && (
          <button
            type="button"
            disabled={selectedCount === 0 || retrying}
            onClick={retryMigration}
            className="tw-cursor-pointer tw-rounded tw-border tw-border-base-300 tw-px-3 tw-py-1.5 tw-text-sm disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
          >
            {t('administrationPanel-databases.migration-retry')}
          </button>
        )}
      </AdminToolbar>
      <AdminTable
        idPrefix="database"
        rows={databases}
        columns={columns}
        rowId={(database) => database.id}
        selectable={isAdmin()}
        isLoading={query.isLoading}
        orderBy={orderBy}
        descending={descending}
        onSort={sort}
        footer={
          <div className="tw-flex-shrink-0 tw-border-t tw-border-base-300">
            <Pagination
              currentPage={page}
              totalItems={total}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size)
                setPage(1)
              }}
            />
          </div>
        }
      />
      {transferTarget && (
        <TransferDatabaseModal
          isOpen={true}
          close={() => setTransferTarget(null)}
          databaseId={Number(transferTarget.id)}
          databaseName={transferTarget.name}
          currentServerId={Number(transferTarget.serverId)}
          onTransferred={() => query.refetch()}
        />
      )}
    </div>
  )
}

export const AdminDatabasesPage: React.FC<Props> = (props) => (
  <TableProvider>
    <AdminDatabasesPageContent {...props} />
  </TableProvider>
)
